import { useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { resolveHelpTopic } from '../../domain/help/helpTopics';
import HelpDrawer from './HelpDrawer';

interface HelpButtonProps {
    /** Whether the persistent sidebar drawer is currently collapsed (56px vs 180px wide). */
    collapsed?: boolean;
}

/**
 * Floating, route-aware help button rendered once for the whole app. It opens
 * the documentation drawer for whichever page the user is currently on, so
 * every page gets an info button without any per-page changes. Positioned
 * bottom-left, just past the persistent sidebar, to avoid overlapping the
 * Sumator widget (bottom-right). The sidebar is a MUI `Drawer`, whose default
 * z-index (1200) sits above an unadorned Fab, so this must be raised above it
 * or it renders invisibly underneath the sidebar panel.
 */
const HelpButton = ({ collapsed = false }: HelpButtonProps) => {
    const { t } = useTranslation('help');
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const topic = resolveHelpTopic(location.pathname);

    return (
        <>
            <Tooltip title={t('button.tooltip')} placement="right">
                <Fab
                    color="primary"
                    size="medium"
                    aria-label={t('button.ariaLabel')}
                    onClick={() => setOpen(true)}
                    data-testid="help-button"
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        left: collapsed ? 72 : 196,
                        zIndex: theme => theme.zIndex.drawer + 1,
                    }}
                >
                    <HelpIcon />
                </Fab>
            </Tooltip>
            <HelpDrawer open={open} topicId={topic.id} onClose={() => setOpen(false)} />
        </>
    );
};

export default HelpButton;
